// src/redux/clientsSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  crudFactoryV2,
  addCrudExtraReducersV2,
} from "../helpers/CrudToolkitV2";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const getHeaders = (body) => {
  const isFormData = body instanceof FormData;

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    Lang: i18n.language,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
};

// ✅ Custom update thunk for clients
export const updateClients = createAsyncThunk(
  "clients/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      if (data instanceof FormData) {
        data.append("client_id", id);
      }

      const res = await axios.post(
        `${BASE_URL}/clients/update`,
        data, // pass raw FormData directly
        {
          headers: getHeaders(data),
        }
      );

      return { ...res.data, client_id: id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to update");
    }
  }
);

// ✅ Create thunks using the updated CRUD factory
const { store, getById, remove, restore, fetchDeleted } = crudFactoryV2("clients", {
  withRestore: true,
  withDeleted: true,
});

const mapClientToNameOption = (client) => ({
  value: String(client?.name || "").trim(),
  label: String(client?.name || client?.number || "").trim(),
});

const mergeNameOptions = (existing, incoming) => {
  const seen = new Set(existing.map((o) => o.value));
  const next = [...existing];
  incoming.forEach((option) => {
    if (!option.value || seen.has(option.value)) return;
    seen.add(option.value);
    next.push(option);
  });
  return next;
};

export const fetchClientFilterOptions = createAsyncThunk(
  "clients/fetchFilterOptions",
  async (
    { page = 1, per_page = 20, search = "", append = false } = {},
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("per_page", String(per_page));
      queryParams.append("page", String(page));
      const term = String(search || "").trim();
      if (term) {
        queryParams.append("search", term);
        queryParams.append("name", term);
      }
      const qs = queryParams.toString();
      const res = await axios.get(
        `${BASE_URL}/clients${qs ? `?${qs}` : ""}`,
        { headers: getHeaders() }
      );
      const payload = res.data.data;
      const rows = Array.isArray(payload?.data) ? payload.data : [];
      return {
        options: rows.map(mapClientToNameOption).filter((o) => o.value),
        pagination: payload?.pagination || null,
        append: Boolean(append),
      };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch client names"
      );
    }
  }
);

export const fetchClients = createAsyncThunk(
  "clients/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const merged = { per_page: 20, page: 1, ...params };
      Object.keys(merged).forEach((key) => {
        const value = merged[key];
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });
      const qs = queryParams.toString();
      const res = await axios.get(
        `${BASE_URL}/clients${qs ? `?${qs}` : ""}`,
        { headers: getHeaders() }
      );
      const payload = res.data.data;
      return {
        data: Array.isArray(payload?.data) ? payload.data : [],
        pagination: payload?.pagination || null,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch");
    }
  }
);

// ✅ Initial state
const initialState = {
  clients: { data: [], pagination: null },
  clientFilterOptions: [],
  clientFilterOptionsPagination: null,
  clientFilterOptionsLoading: false,
  deleted: [],
  record: null,
  isLoading: false,
  error: null,
  success: null,
};

// ✅ Create slice
const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    addCrudExtraReducersV2(builder, {
      store,
      getById,
      update: updateClients,
      remove,
      restore,
      fetchDeleted,
      key: "clients",
      idKey: "client_id",
    });

    builder
      .addCase(fetchClients.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.isLoading = false;
        const page = action.meta.arg?.page || 1;
        const incoming = action.payload?.data || [];
        if (page === 1) {
          state.clients = action.payload;
          return;
        }
        const prev = state.clients?.data || [];
        const existingIds = new Set(prev.map((c) => c.id));
        const merged = [...prev];
        incoming.forEach((row) => {
          if (row?.id != null && !existingIds.has(row.id)) merged.push(row);
        });
        state.clients = {
          data: merged,
          pagination: action.payload?.pagination ?? state.clients?.pagination,
        };
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchClientFilterOptions.pending, (state) => {
        state.clientFilterOptionsLoading = true;
      })
      .addCase(fetchClientFilterOptions.fulfilled, (state, action) => {
        state.clientFilterOptionsLoading = false;
        const { options, pagination, append } = action.payload;
        state.clientFilterOptions = append
          ? mergeNameOptions(state.clientFilterOptions, options)
          : options;
        state.clientFilterOptionsPagination = pagination;
      })
      .addCase(fetchClientFilterOptions.rejected, (state) => {
        state.clientFilterOptionsLoading = false;
      });
  },
});

// ✅ Export actions and thunks
export const { clearState } = clientsSlice.actions;

export {
  store as addClients,
  getById as getClients,
  remove as deleteClients,
  restore as restoreClients,
  fetchDeleted as fetchDeletedClients,
};

export default clientsSlice.reducer;
